import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Spinner, XCircle, ArrowLeft } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const T = {
  fr: {
    checking: "Vérification du paiement…", success: "Paiement confirmé !",
    successSub: "Votre acompte a bien été reçu. Un email de confirmation vous a été envoyé. Votre chauffeur VIP vous attend.",
    ref: "Référence", back: "Retour à l'accueil", timeout: "Le paiement prend plus de temps que prévu. Vérifiez votre email ou contactez-nous.",
    cancelled: "Paiement annulé", cancelledSub: "Votre réservation n'a pas été confirmée. Vous pouvez réessayer quand vous le souhaitez.",
  },
  nl: {
    checking: "Betaling controleren…", success: "Betaling bevestigd!",
    successSub: "Uw voorschot is goed ontvangen. Een bevestigingsmail is verstuurd. Uw VIP-chauffeur staat klaar.",
    ref: "Referentie", back: "Terug naar home", timeout: "De betaling duurt langer dan verwacht. Controleer uw e-mail of neem contact op.",
    cancelled: "Betaling geannuleerd", cancelledSub: "Uw reservering is niet bevestigd. U kunt het opnieuw proberen wanneer u wilt.",
  },
};

export function PaymentSuccess() {
  const { lang } = useLang();
  const t = T[lang];
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [bookingId, setBookingId] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("timeout"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); setBookingId(data.booking_id); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch (e) { /* keep polling */ }
      if (attempts.current >= 12) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-ink-900 grain flex items-center justify-center px-6" data-testid="payment-success-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-lg w-full border border-white/10 bg-ink-800 p-10 md:p-14 text-center">
        {status === "checking" && (
          <>
            <Spinner size={56} className="text-gold mx-auto animate-spin" weight="light" />
            <p className="mt-8 text-white/70 font-light" data-testid="payment-checking">{t.checking}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={64} className="text-gold mx-auto" weight="light" />
            <h1 className="mt-6 font-serif text-4xl text-white" data-testid="payment-success-title">{t.success}</h1>
            <p className="mt-4 text-white/60 font-light">{t.successSub}</p>
            {bookingId && <p className="mt-6 overline text-[10px] text-white/40">{t.ref}: {bookingId.slice(0, 8).toUpperCase()}</p>}
          </>
        )}
        {(status === "timeout" || status === "failed") && (
          <>
            <XCircle size={64} className="text-white/40 mx-auto" weight="light" />
            <p className="mt-6 text-white/70 font-light">{t.timeout}</p>
          </>
        )}
        <Link to="/" data-testid="back-home-btn" className="mt-10 inline-flex items-center gap-2 border border-white/20 hover:border-gold text-white overline text-xs px-8 py-4 transition-colors">
          <ArrowLeft weight="light" size={16} /> {t.back}
        </Link>
      </motion.div>
    </div>
  );
}

export function PaymentCancel() {
  const { lang } = useLang();
  const t = T[lang];
  return (
    <div className="min-h-screen bg-ink-900 grain flex items-center justify-center px-6" data-testid="payment-cancel-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-lg w-full border border-white/10 bg-ink-800 p-10 md:p-14 text-center">
        <XCircle size={64} className="text-white/40 mx-auto" weight="light" />
        <h1 className="mt-6 font-serif text-4xl text-white">{t.cancelled}</h1>
        <p className="mt-4 text-white/60 font-light">{t.cancelledSub}</p>
        <Link to="/" data-testid="cancel-back-home-btn" className="mt-10 inline-flex items-center gap-2 bg-gold hover:bg-gold-champagne text-black overline text-xs px-8 py-4 transition-colors">
          <ArrowLeft weight="light" size={16} /> {t.back}
        </Link>
      </motion.div>
    </div>
  );
}
